"use client";

import { Heading, Image, Link, Text } from "@pamperedchef/design-system";

import { ButtonLink } from "@/composites/foundation";

import * as styles from "./CategoryTiles.module.scss";

export interface CategoryTile {
	/** The category's authored code — stable, and unique within the grid. */
	code: string;
	name: string;
	href: string;
	image?: { src: string; alt: string };
	/** Live count of products in the category. Omit to render no count. */
	itemCount?: number;
}

export interface CategoryTilesShopAll {
	itemCount?: number;
	href: string;
}

export interface CategoryTilesProps {
	/** Authored on the layout. Defaults to the wording the design uses. */
	heading?: string;
	tiles: CategoryTile[];
	/** Off hides every count even where one was supplied. */
	showItemCounts?: boolean;
	shopAll?: CategoryTilesShopAll;
	/** Renders a labelled placeholder as the last cell when set. */
	trailingPlaceholder?: string;
	/** `cover` fills the tile with a photo; `contain` keeps a product silo whole. */
	imageFit?: "contain" | "cover";
	layout?: "grid" | "row";
}

const DEFAULT_HEADING = "Shop by Subcategory";

function itemsLabel(count: number): string {
	return count === 1 ? "1 item" : `${count} items`;
}

function shopAllLabel(count: number | undefined): string {
	if (count === undefined) return "Shop All";
	return count === 1 ? "Shop All 1 Item" : `Shop All ${count} Items`;
}

/**
 * The "Shop by Subcategory" grid. Receives finished hrefs and an already-ordered
 * list — ordering and link building belong to the caller, which is the only part
 * that knows the taxonomy.
 */
export function CategoryTiles({
	heading = DEFAULT_HEADING,
	tiles,
	showItemCounts = true,
	shopAll,
	trailingPlaceholder,
	imageFit = "contain",
	layout = "grid"
}: CategoryTilesProps): JSX.Element | null {
	if (tiles.length === 0) return null;

	return (
		<section
			className={styles["tiles"]}
			data-component="CategoryTiles"
			data-image-fit={imageFit}
			data-layout={layout}
		>
			{heading || shopAll ? (
				<div className={styles["tiles__header"]}>
					{heading ? (
						<Heading tag="h2" className={styles["tiles__heading"]}>
							{heading}
						</Heading>
					) : null}

					{shopAll ? (
						<ButtonLink
							kind="secondary"
							size="lg"
							className={styles["tiles__shop-all"]}
							href={shopAll.href}
							data-shop-all
						>
							{shopAllLabel(shopAll.itemCount)}
						</ButtonLink>
					) : null}
				</div>
			) : null}

			<ul className={styles["tiles__grid"]}>
				{tiles.map((tile) => (
					<li
						key={tile.code}
						className={styles["tile"]}
						data-category={tile.code}
					>
						<a
							href={tile.href}
							aria-hidden="true"
							tabIndex={-1}
							className={styles["tile__image-link"]}
						>
							{tile.image ? (
								<Image
									src={tile.image.src}
									alt={tile.image.alt}
									className={styles["tile__image"]}
								/>
							) : (
								<span className={styles["tile__placeholder"]} />
							)}
						</a>

						<div className={styles["tile__body"]}>
							<Link
								href={tile.href}
								size="lg"
								className={styles["tile__name"]}
							>
								{tile.name}
							</Link>

							{showItemCounts && tile.itemCount !== undefined ? (
								<Text
									tag="p"
									className={styles["tile__count"]}
									data-item-count={tile.itemCount}
								>
									{itemsLabel(tile.itemCount)}
								</Text>
							) : null}
						</div>
					</li>
				))}

				{trailingPlaceholder ? (
					<li
						className={styles["tile"]}
						data-placeholder={trailingPlaceholder}
					>
						<span className={styles["tile__stub"]}>
							<Text tag="span" className={styles["tile__name"]}>
								{trailingPlaceholder}
							</Text>
						</span>
					</li>
				) : null}
			</ul>
		</section>
	);
}
